import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MODE_PAR_DEFAUT, type SorteDeLien } from '@nextilt/commun';
import type { Bulle } from '../partage/api/types';
import { BarreHierarchisation, useHierarchisation } from '../hierarchie';
import { vueMontree } from './ligneAuPouce';
import { Inventaire } from '../inventaire';
import {
  BarreLier,
  BoutonLier,
  Cadenas,
  SelecteurProfondeur,
  useCalque,
  useLiaison,
  useProfondeur,
  type PoseDesPapiers,
} from '../carte';
import type { ArriveeSurLaFeuille } from '../partage/navigationDeLaFeuille';
import { AJOUTER_UNE_BULLE_ICI, BULLE, motsDeSituation, motsDuDossier, type Rangement } from '../partage/vocabulaire';
import { gesteDuRond } from './rondQuiCoche';
import { useAuPouce, useCouche } from '../partage/surface';
import { useFeuille, usePoserRangs } from '../partage/useArbre';
import { useFicheDeLaFeuille, FicheDeLaFeuille } from '../fiche';
import { useNavigationDeLaFeuille } from './useNavigationDeLaFeuille';
import { BarreDeLaFeuille } from './BarreDeLaFeuille';
import { useVueDeFeuille } from './BasculeVue';
import { angleALEntree, montreLArbre } from './angles';
import { montrerCeQuiEstFait, reglerCeQuiEstFait } from './arbreAuPouce';
import { SituationDeFeuille } from './SituationDeFeuille';
import { TeteDeFeuille } from './TeteDeFeuille';
import { HabitDuProjet } from './HabitDuProjet';
import { habitReglable } from './habit';
import { VuesDeLaFeuille } from './VuesDeLaFeuille';
import { FilDuPouce } from './DetailDUneBranche';
import { useGestesDeFeuille } from './gestesDeFeuille';
import { useTouchesDeLaCarte } from './useTouchesDeLaCarte';
import { gesteDeLaTouche } from './modes';
import { ChercherDansLaCarte } from './TouchesDeLaCarte';
import { MargesDeLaCarte } from './MargesDeLaCarte';
import { AnalyseCouchee } from './AnalyseCouchee';
import { HierarchieCouchee } from './HierarchieCouchee';
import { choisirLaVueCouchee, prendLaVueCouchee, vueCouchee, type VueCouchee } from './vueCouchee';
import { PanneauDAnalyse } from './PanneauDAnalyse';
import { signalerLaFeuille } from '../aide';
import { useAngleDesGestes } from '../partage/useContexteDesGestes';
import { useLecturesDeLApp } from '../partage/useLecturesDeLApp';
import { teinteOuAccent } from '../apparence';
import { SANS_LOUPE } from '../partage/loupeDeLaCarte';
import { useApparence } from '../partage/apparence';
import type { Marge } from './modes';

/** Aucune marge ouverte : hors de la Carte et de la Hiérarchie. */
const AUCUNE_MARGE: ReadonlySet<Marge> = new Set();
import './Feuille.css';

/**
 * La feuille d'une bulle : sa surface. On entre dedans, elle devient le centre, et on y
 * ajoute comme dans un projet — il n'existe pas de type terminal (SPEC principe 7).
 *
 * Ce fichier ne dessine presque rien : il **aiguille**. Il tient ce qui ne peut vivre
 * qu'ici — l'adresse où l'on est, la vue courante — et il décide qui parle. La navigation
 * (quelle fiche est ouverte, d'où l'on arrive) est une règle à part
 * (`partage/navigationDeLaFeuille.ts`) ; la tête, la situation, les vues, la barre du bas,
 * la fiche et les suites d'un geste se tiennent chacune à leur place.
 *
 * Une règle gouverne l'aiguillage : **aucune des vues n'est la vraie**. Rien ne doit être
 * visible dans l'une et absent de l'autre, sinon la bascule devient un piège — et elle
 * conserve la sélection, parce qu'on cesse de s'en servir en deux jours si on y perd son fil.
 */
export function Feuille() {
  const { id = '' } = useParams();
  const naviguer = useNavigate();
  const feuille = useFeuille(id);
  /*
   * Le téléphone couché, dans un projet ou une branche (chantier telephone-couche, 01) : la vue
   * couchée, ni celle du pouce, ni les vues de l'ordinateur (`vueCouchee.ts`).
   */
  const couche = useCouche();
  const enCouche = couche && !!feuille.data && prendLaVueCouchee(feuille.data);
  const auPouce = useAuPouce() && !enCouche;
  // L'Analyse ou la Hiérarchie, couché : dure la visite.
  const [vueDuCouche, setVueDuCouche] = useState<VueCouchee>(vueCouchee);
  const choisirVueDuCouche = (v: VueCouchee) => {
    choisirLaVueCouchee(v);
    setVueDuCouche(v);
  };
  // Au téléphone, plus bas que le projet, le haut reste celui du projet : sa feuille, déjà lue en venant de l'arbre.
  const racineLue = feuille.data && feuille.data.chemin.length > 1 ? feuille.data.chemin[0]!.id : '';
  const projetDuHaut = useFeuille(auPouce ? racineLue : '');
  const poserRangs = usePoserRangs();
  const gestes = useGestesDeFeuille();
  // Après une bulle écrite dans la barre : écrire la suivante, ou ouvrir sa fiche (Paramètres).
  const { apresUneBulle } = useApparence();

  /*
   * Quelle fiche est ouverte, ce qu'on a gardé au rond, et ce que demande l'arrivée : d'où l'on
   * vient de remonter (la carte teinte cette branche : sans elle, la profondeur se perd au
   * moment précis où elle vient d'être parcourue), une fiche à ouvrir, la Carte.
   */
  const navigation = useNavigationDeLaFeuille(id);
  const { surFiche, allerALaFiche, ouvrirDAilleurs } = useFicheDeLaFeuille(feuille.data, navigation);

  /*
   * La touche de la bascule se tait dès qu'un panneau est ouvert : dans une fiche, « v »
   * s'écrit dans les notes, et basculer la vue DERRIÈRE le panneau est un geste que
   * personne n'a demandé. Au téléphone, la liste seulement (chantier `telephone-cadre`,
   * ticket 04) : ni bascule, ni touche v, et une arrivée qui demande la Carte montre la liste.
   */
  const panneauOuvert = navigation.fiche !== null || gestes.inventaire !== null;
  const { vue: vueChoisie, setVue } = useVueDeFeuille(panneauOuvert || auPouce || enCouche);
  // Couché, ni la Carte ni ses touches : la vue de l'ordinateur reste en Liste derrière.
  const vue = vueMontree(vueChoisie, auPouce || enCouche);

  /*
   * L'angle à l'ouverture (chantier angle-a-l-ouverture, modification 02) : entrer dans un projet
   * l'ouvre sur l'angle de son habit, descendre dans une étape garde l'angle où l'on est
   * (`angleALEntree`). Posé pendant le rendu, dès que la feuille est lue, comme la navigation
   * (`useNavigationDeLaFeuille`) : la feuille ne se dessine pas d'abord sur la Carte. À
   * l'ordinateur seulement ; une arrivée qui demande la Carte (l'Aide) l'emporte.
   */
  const projetLu = feuille.data?.chemin[0]?.id ?? null;
  const [projetVu, setProjetVu] = useState<string | null>(null);
  if (projetLu !== null && projetLu !== projetVu && feuille.data) {
    setProjetVu(projetLu);
    const angle =
      auPouce || enCouche || navigation.arrivee.enCarte === true
        ? null
        : angleALEntree(projetVu, projetLu, feuille.data.habit.angle);
    if (angle !== null) setVue(angle);
  }

  /*
   * Une partie de l'Aide commence sur la carte (ticket 17 de retours-testeurs) : dans un niveau
   * lancé en entier, la partie suivante arrive sur la même feuille, qui ne se remonte pas, et
   * gardait la vue où la précédente avait fini. La vue est tenue par `useVueDeFeuille`.
   */
  const enCarte = navigation.arrivee.enCarte === true;
  useEffect(() => {
    if (enCarte) setVue('carte');
  }, [navigation.cle, enCarte, setVue]);

  // Ce que la feuille montre, pour le tuto : ses étapes d'écran s'y remplissent.
  useEffect(() => {
    signalerLaFeuille({ id, fiche: navigation.fiche, vue });
  }, [id, navigation.fiche, vue]);
  useEffect(() => () => signalerLaFeuille(null), []);

  /*
   * Ce que la feuille montre, pour le journal des gestes (chantier journal-des-gestes, ticket 05) :
   * l'angle affiché part avec chaque geste ; le projet, la fiche ouverte et l'angle deviennent des
   * lectures (ouvrir, fermer, changer d'angle). Seulement la feuille lue, et la fiche montrée.
   */
  useAngleDesGestes(vue);
  useLecturesDeLApp(
    projetLu !== null ? { projet: projetLu, feuille: id, fiche: surFiche?.bulle.id ?? null, angle: vue } : null
  );

  /*
   * La profondeur et ce qui est ouvert sur place, en Carte comme en Hiérarchie (ticket 13 de
   * retours-testeurs) : un seul état pour les deux vues, tenu tant qu'on reste dans le
   * dossier (le premier maillon du chemin), calculé sur les bulles que la carte connaît, le
   * premier tour dans l'ordre de lecture de ses axes. La priorité mise en avant (ticket 04 du
   * chantier `priorites`), tenue de même.
   */
  const bullesConnues = useMemo(() => {
    const donnees = feuille.data;
    if (!donnees) return [];
    const axe = (i: string) => donnees.carte.axes[i] ?? 0;
    const premierTour = [...donnees.enfants].sort((a, b) => axe(a.id) - axe(b.id));
    return [...premierTour, ...donnees.carte.dessous];
  }, [feuille.data]);
  const profondeur = useProfondeur(feuille.data?.chemin[0]?.id, id, bullesConnues);
  const calque = useCalque(feuille.data?.chemin[0]?.id);

  // « Montrer ce qui est fait », du ⚙ d'un projet au téléphone (chantier telephone-projet, 02) : tient la session.
  const [montrerLesFaites, setMontrerLesFaites] = useState(montrerCeQuiEstFait);
  const basculerLesFaites = (oui: boolean) => {
    reglerCeQuiEstFait(oui);
    setMontrerLesFaites(oui);
  };

  // « Ranger » de la Liste (chantier `revue-design`, ticket 03) : tient d'une feuille à l'autre.
  const [rangement, setRangement] = useState<Rangement>('mon_ordre');

  // H n'est une touche que si le mode de la carte la garde (Analyse ne la garde pas, chantier
  // theme-analyse, ticket 01) ; hors de la Carte et de la Hiérarchie, ou au pouce, elle reste.
  const modeDeLaCarte = feuille.data?.mode.aAppliquer ?? MODE_PAR_DEFAUT;

  /*
   * « Lier » (chantier liens-entre-bulles, ticket 04) : sur la Carte, à l'ordinateur, dans un mode
   * qui garde J (pas en Analyse, comme H), quand il y a au moins deux bulles à relier. Le bouton
   * « Lier », sur le bord droit de la carte (une flèche, et trois au survol), ou J ; jamais pendant Hiérarchiser, ni Hiérarchiser pendant lui.
   */
  const lierIci =
    !auPouce &&
    !enCouche &&
    vue === 'carte' &&
    bullesConnues.length > 1 &&
    gesteDeLaTouche(modeDeLaCarte, 'j')?.sorte === 'lier';
  const liaison = useLiaison(lierIci, id);
  /*
   * Le cadenas de la Carte (deplacer-les-bulles, ticket 03) : ouvert pour la feuille où on l'a
   * ouvert ; quitter la Carte le referme, comme la page de la carte qu'on quitte.
   */
  const [cadenas, setCadenas] = useState<{ ici: string; ouvert: boolean }>({ ici: '', ouvert: false });
  const [vueDuCadenas, setVueDuCadenas] = useState(vue);
  if (vueDuCadenas !== vue) {
    setVueDuCadenas(vue);
    if (cadenas.ouvert) setCadenas({ ici: '', ouvert: false });
  }

  const hSurLaCarte = !auPouce && (vue === 'carte' || vue === 'hierarchie');
  const hierarchisation = useHierarchisation(
    (rangs) => poserRangs.mutate([...rangs].map(([idBulle, rang]) => ({ id: idBulle, rang }))),
    !enCouche && !liaison.actif && (!hSurLaCarte || gesteDeLaTouche(modeDeLaCarte, 'h')?.sorte === 'hierarchiser')
  );
  const titres = useMemo(() => new Map(bullesConnues.map((b) => [b.id, b.titre])), [bullesConnues]);

  // Le champ de la barre de frappe, où « + Bulle » pose le curseur.
  const champAjout = useRef<HTMLInputElement>(null);

  /*
   * En Carte et en Hiérarchie, à l'ordinateur, la barre de frappe est rangée derrière « + Bulle »,
   * comme la maquette du projet ouvert (`.ajout-b`) : un clic la fait sortir, le curseur dedans ;
   * Échap, ou quitter le champ vide, la range (retour d'Igor du 2026-10-20 : « avoir le bouton
   * comme sur la maquette et quand tu clique dessus la barre sort »). Une autre feuille l'ouvre
   * rangée.
   */
  const barreSurLeFond = !auPouce && (vue === 'carte' || vue === 'hierarchie');
  const [barreSortie, setBarreSortie] = useState(false);
  useEffect(() => setBarreSortie(false), [id]);
  useEffect(() => {
    if (!barreSortie) return;
    const champ = champAjout.current;
    if (!champ) return;
    champ.focus();
    const ranger = () => {
      if (!champ.value.trim()) setBarreSortie(false);
    };
    const echap = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setBarreSortie(false);
    };
    champ.addEventListener('blur', ranger);
    champ.addEventListener('keydown', echap);
    return () => {
      champ.removeEventListener('blur', ranger);
      champ.removeEventListener('keydown', echap);
    };
  }, [barreSortie]);

  // « Recentrer » de la Carte, que la carte donne en s'ouvrant et reprend en partant : il vit
  // dans le menu de Profondeur, sur la ligne des angles (chantier tete-du-projet).
  const [recentrerLaCarte, setRecentrerLaCarte] = useState<(() => void) | null>(null);
  const donnerRecentrer = useCallback((recentrer: (() => void) | null) => setRecentrerLaCarte(() => recentrer), []);

  /*
   * Les touches de la carte (chantier modes-de-la-carte, modification 02) : selon le mode de la
   * feuille ouverte (le sien, sinon celui de ses mères, deuxième passe), sur la Carte et la Hiérarchie, à l'ordinateur, sans fiche ni inventaire ouverts.
   */
  const poseDesPapiers = useRef<PoseDesPapiers | null>(null);
  const surLaCarte = !auPouce && (vue === 'carte' || vue === 'hierarchie');
  /*
   * J : lance « Lier » quand rien d'autre n'est ouvert (ni fiche, ni menu, ni Hiérarchiser) ; en
   * cours, l'arrête, sauf sous le refus d'une boucle, qui se ferme d'abord.
   */
  const basculerLier = (libre: boolean) => {
    if (liaison.actif) {
      if (liaison.refusOuvert) return false;
      liaison.arreter();
      return true;
    }
    if (!libre || !lierIci || hierarchisation.actif) return false;
    liaison.commencer();
    return true;
  };
  const touches = useTouchesDeLaCarte({
    mode: modeDeLaCarte,
    actives: surLaCarte && !panneauOuvert && !!feuille.data,
    projet: projetLu,
    poseDesPapiers,
    feuille: feuille.data,
    surLaCarte: !auPouce && vue === 'carte',
    ouvertes: profondeur.ouvertes,
    lier: basculerLier,
  });

  if (feuille.isPending) return <div className="feuille" />;

  if (feuille.isError || !feuille.data) {
    return (
      <div className="feuille feuille--perdue">
        <p>Cette bulle n’existe plus.</p>
        <button className="bouton bouton-discret" onClick={() => naviguer('/dossiers')}>
          {motsDuDossier(auPouce).revenirAux}
        </button>
      </div>
    );
  }

  const { bulle, chemin, enfants, couleur, habit, fiches } = feuille.data;
  const parent = (chemin.length > 1 ? chemin[chemin.length - 2] : null) ?? null;
  const situation = motsDeSituation(bulle.situation);
  const entrer = (i: string) => naviguer(`/feuille/${i}`);
  const auDessus = parent ? `/feuille/${parent.id}` : '/dossiers';
  /*
   * Au téléphone, dans un projet, une tâche (rien dedans) se lit en page, à sa propre adresse
   * (chantier telephone-projet, 04, maquette `projet()`, la branche `if (ta)`) : le fil, puis sa
   * fiche, le même contenu que la fiche qui monte. Le retour du téléphone ramène où l'on était.
   */
  const ficheEnPage = auPouce && parent !== null && enfants.length === 0;

  /*
   * Le rond d'une bulle de la Liste (ticket 05 du chantier telephone) : au téléphone, le même
   * « C'est fait » et le même « Annuler » que la fiche ; sur ordinateur, le tour des états.
   */
  // Couché, la case d'une carte coche comme au pouce (chantier telephone-couche, 02).
  const auDoigt = auPouce || enCouche;
  const toucherLeRond = (b: Bulle) => {
    const geste = gesteDuRond(b.etat, auDoigt);
    if (geste.sorte === 'cocher') {
      if (auDoigt) navigation.garder(b.id);
      gestes.validerBulle(b.id);
    } else if (geste.sorte === 'decocher') gestes.defaire(b);
    else gestes.changerEtat(b.id, geste.etat);
  };
  /*
   * En Liste, sur l'ordinateur, la fiche s'ouvre À CÔTÉ de la Liste (décision 2 de la Liste) :
   * la Liste se resserre à sa gauche et reste cliquable, une autre ligne ouvre sa fiche.
   */
  const ficheACote = vue === 'liste' && !auPouce && surFiche !== null;
  /*
   * Hiérarchiser reste sur l'ordinateur (chantier `telephone-cadre`, ticket 04, défaut accepté
   * du cadrage, décision 20) : au téléphone, ni « Hiérarchiser », ni sa barre, ni la Liste qui se
   * désigne, même si la touche h l'a allumé d'un clavier branché.
   */
  // Les angles Agenda et Maintenant n'ont rien à désigner (chantier angles-du-projet, tickets 02 et 03) : ni la barre, ni le bouton.
  const horsDeLArbre = !montreLArbre(vue);
  const enHierarchisation = !auPouce && !enCouche && !horsDeLArbre && hierarchisation.actif;
  const hierarchisable =
    !auPouce && !enCouche && !horsDeLArbre && enfants.length > 1 && !hierarchisation.actif && !liaison.actif;

  /*
   * Les outils du projet, sur la ligne des angles, tout à droite (chantier tete-du-projet,
   * modification 01, retour d'Igor du 2026-10-20, maquette du carnet `projet-ouvert.html`,
   * `.outils`) : le bouton « Profondeur 1 » en Carte et en Hiérarchie, quand il y a de quoi
   * creuser (son menu porte « Recentrer » en Carte) ; l'habit du projet (chantier
   * habit-du-projet, ticket 02) dans le projet lui-même, dans chaque angle. Au téléphone, ni
   * profondeur ni habit (décision d'Igor du 2026-09-28) : on creuse en entrant, un niveau à la
   * fois, et seule la liste se montre.
   */
  const avecProfondeur = !auPouce && (vue === 'carte' || vue === 'hierarchie') && enfants.length > 0;
  const avecHabit = habitReglable(parent, auPouce);
  // « Mode » dans la tête de toute feuille, à l'ordinateur (modes-de-la-carte, deuxième passe de 01 et 02).
  const avecMode = !auPouce;
  // Le bouton « Lier » ouvre « Lier » ; une fiche ouverte se ferme d'abord (pendant Lier, aucune ne s'ouvre).
  const avecLier = lierIci && !hierarchisation.actif;
  const basculerLeBoutonLier = () => {
    if (liaison.actif) return liaison.arreter();
    if (navigation.fiche !== null) navigation.fermer();
    liaison.commencer();
  };
  // Une des trois flèches : « Lier » s'ouvre sur sa sorte, ou change de sorte s'il est ouvert.
  const lierAvecLaSorte = (sorte: SorteDeLien) => {
    if (liaison.actif) return liaison.choisirLaSorte(sorte);
    if (navigation.fiche !== null) navigation.fermer();
    liaison.commencer(sorte);
  };
  /*
   * Le cadenas (chantier deplacer-les-bulles, ticket 03) : sur la Carte de l'ordinateur, sous la
   * flèche de « Lier », caché avec elle quand une fiche est ouverte à droite, et en Regrouper.
   * Fermé à chaque feuille ouverte et à chaque retour sur la Carte : il ne reste ouvert que pour
   * la feuille et la vue où on l'a ouvert. Pendant Lier ou Hiérarchiser, il reste là, sans effet.
   */
  const cadenasIci = id;
  const avecCadenas =
    !auPouce &&
    !enCouche &&
    vue === 'carte' &&
    enfants.length > 0 &&
    navigation.fiche === null &&
    !(surLaCarte && touches.colonnes);
  const cadenasOuvert = avecCadenas && cadenas.ici === cadenasIci && cadenas.ouvert;
  const basculerLeCadenas = () => setCadenas({ ici: cadenasIci, ouvert: !cadenasOuvert });
  const outils =
    avecProfondeur || avecMode ? (
      <>
        {avecProfondeur && (
          <SelecteurProfondeur
            niveaux={profondeur.niveaux}
            actuelle={profondeur.actuelle}
            onChoisir={profondeur.choisir}
            onRecentrer={vue === 'carte' ? recentrerLaCarte : null}
          />
        )}
        {avecMode && (
          <HabitDuProjet
            bulleId={bulle.id}
            projet={avecHabit ? { couleur, habit, fiches } : null}
            mode={feuille.data.mode}
            mere={parent?.titre ?? null}
          />
        )}
      </>
    ) : null;

  /* « Ouvrir sa fiche » (chantier modes-de-la-carte, modification 04) : la bulle écrite ouvre sa fiche, sur place. */
  const ecriteOuvreSaFiche = (i: string) => navigation.ouvrir(i);

  const barre = (
    <BarreDeLaFeuille
      id={id}
      titre={bulle.titre}
      couleur={couleur}
      vive={enfants.length === 0}
      champ={champAjout}
      auPouce={auPouce}
      onEcrite={apresUneBulle === 'fiche' ? ecriteOuvreSaFiche : undefined}
    />
  );

  return (
    <div
      className={`feuille${ficheACote ? ' feuille--fiche-a-cote' : ''}${enCouche ? ' feuille--couchee' : ''}`}
      style={{ ['--teinte' as string]: teinteOuAccent(couleur) }}
    >
      <TeteDeFeuille
        bulle={bulle}
        chemin={chemin}
        parent={parent}
        decompte={bulle.decompte}
        /* Le ⚙ de la Hiérarchie, sur l'ordinateur seulement (chantiers `revue-design`, tickets 03
           et 04, et `telephone-cadre`, ticket 04) : caché depuis le chantier `tete-du-projet`. */
        avecParametres={!auPouce && vue === 'hierarchie'}
        vue={vue}
        onVue={setVue}
        hierarchisable={hierarchisable}
        onHierarchiser={hierarchisation.commencer}
        calque={!auPouce && vue === 'hierarchie' && enfants.length > 0 ? calque : undefined}
        onRemonter={() => naviguer(auDessus, { state: { venantDe: bulle.id } satisfies ArriveeSurLaFeuille })}
        onMaillon={entrer}
        onSaFiche={() => navigation.ouvrir(bulle.id)}
        outils={outils}
        montrerCeQuiEstFait={montrerLesFaites}
        onMontrerCeQuiEstFait={basculerLesFaites}
        couche={enCouche ? { vue: vueDuCouche, onVue: choisirVueDuCouche } : null}
        projet={
          parent && chemin[0]
            ? {
                titre: chemin[0].titre,
                decompte: projetDuHaut.data?.bulle.decompte ?? null,
                onSaFiche: () => void allerALaFiche(chemin[0]!.id, { arrivee: 'chez-elle' }),
              }
            : null
        }
      />

      {situation && !enCouche && (
        <SituationDeFeuille
          situation={situation}
          nom={bulle.situation.nom}
          bulle={bulle}
          onConfirmer={() => gestes.validerBulle(bulle.id)}
          onRelancer={() => gestes.relancer(bulle.id)}
        />
      )}

      {enHierarchisation && (
        <div className="feuille-mode">
          <BarreHierarchisation
            poses={hierarchisation.rangs?.size ?? 0}
            total={enfants.length}
            onEnregistrer={hierarchisation.enregistrer}
            onRenoncer={hierarchisation.arreter}
          />
        </div>
      )}


      {/* Le bouton « Lier » sur le bord droit de la carte ; caché tant qu'une fiche est ouverte à droite. */}
      {avecLier && navigation.fiche === null && (
        <BoutonLier
          actif={liaison.actif}
          sorte={liaison.sorte}
          onBasculer={basculerLeBoutonLier}
          onChoisir={lierAvecLaSorte}
        />
      )}
      {avecCadenas && <Cadenas ouvert={cadenasOuvert} onBasculer={basculerLeCadenas} />}


      {/*
       * La scène : la carte (ou l'angle choisi) et, à l'ordinateur, la fiche posée dedans, à droite,
       * sous la tête du projet (chantier types-de-fiche, modification 02, retour d'Igor du
       * 2026-10-20 : « je veux que ça reste dans la carte pas que ça depasse »). La tête, la
       * profondeur et l'habit restent à leur place ; la fiche ne dépasse pas du cadre de la carte.
       */}
      <div className="feuille-scene">
        {/* Le bandeau de « Lier », posé sur la carte sans la déplacer (retour d'Igor du 2026-10-24). */}
        {liaison.actif && (
          <div className="feuille-pose">
            <BarreLier liaison={liaison} titreDe={(i) => titres.get(i) ?? ''} />
          </div>
        )}
        {enCouche ? (
          // Le corps couché : l'Analyse (02) ou la Hiérarchie (03).
          <div className={`feuille-couchee-corps feuille-couchee-corps--${vueDuCouche}`}>
            {vueDuCouche === 'analyse' ? (
              <AnalyseCouchee feuille={feuille.data} onFiche={navigation.ouvrirSurPlace} onRond={toucherLeRond} />
            ) : (
              <HierarchieCouchee feuille={feuille.data} onEntrer={entrer} onFiche={navigation.ouvrirSurPlace} />
            )}
          </div>
        ) : ficheEnPage ? (
          <div className="feuille-contenu feuille-contenu--page">
            <FilDuPouce chemin={chemin} onMaillon={entrer} />
            <FicheDeLaFeuille
              fiche={{ bulle, chemin }}
              centreId={bulle.id}
              couleur={couleur}
              fichesDuProjet={feuille.data.fiches}
              habitDuProjet={feuille.data.habit}
              gestes={gestes}
              navigation={navigation}
              allerALaFiche={allerALaFiche}
              onEntrer={entrer}
              onRemonterApresSuppression={() => naviguer(auDessus, { replace: true })}
              enPage
              onFermerLaPage={() => naviguer(auDessus)}
            />
          </div>
        ) : (
          <VuesDeLaFeuille
            donnees={feuille.data}
            vue={vue}
            auPouce={auPouce}
            montrerCeQuiEstFait={montrerLesFaites}
            rangement={rangement}
            onRanger={setRangement}
            ficheOuverte={surFiche?.bulle.id ?? null}
            hierarchisation={hierarchisation}
            enHierarchisation={enHierarchisation}
            onRecentrer={donnerRecentrer}
            profondeur={profondeur}
            calque={calque}
            venantDe={navigation.arrivee.venantDe ?? null}
            onEntrer={entrer}
            onFermerLaFiche={navigation.fermer}
            onFiche={navigation.ouvrirSurPlace}
            onSaFiche={navigation.ouvrir}
            /* Les angles Agenda et Maintenant : la fiche de la ligne, sur place quand elle est sous la
               feuille, sinon chez elle. */
            onFicheDUnAngle={(i) => void allerALaFiche(i, { arrivee: 'chez-elle' })}
            onEtat={gestes.changerEtat}
            onRond={toucherLeRond}
            loupe={surLaCarte ? touches.loupe : SANS_LOUPE}
            poseDesPapiers={poseDesPapiers}
            marges={surLaCarte ? touches.marges : AUCUNE_MARGE}
            panneauDAnalyse={surLaCarte && touches.panneauDAnalyse}
            onClicEnAnalyse={surLaCarte && modeDeLaCarte === 'analyse' ? touches.basculerLaBulle : null}
            regroupe={surLaCarte ? touches.colonnes : null}
            liensCachesMontres={surLaCarte && touches.liensCachesMontres}
            lier={liaison.actif ? { premiere: liaison.premiere, onClic: liaison.cliquer } : null}
            onFicheDAilleurs={ouvrirDAilleurs}
            cadenasOuvert={cadenasOuvert}
          />
        )}

        {surLaCarte && touches.panneauDAnalyse && (
          <PanneauDAnalyse
            feuille={feuille.data}
            surlignee={touches.surlignee}
            onSurligner={touches.surlignerUneBulle}
            regroupement={touches.regroupement}
            groupes={touches.groupes}
            onRegrouper={vue === 'carte' ? touches.regrouper : null}
            groupeVu={touches.groupeVu}
            onSurlignerLeGroupe={touches.surlignerLeGroupe}
          />
        )}

        {surLaCarte && (
          <MargesDeLaCarte
            feuille={feuille.data}
            marges={touches.marges}
            contactChoisi={touches.contactChoisi}
            onContact={touches.choisirLeContact}
          />
        )}

        {surLaCarte && touches.recherche !== null && (
          <ChercherDansLaCarte
            valeur={touches.recherche}
            champ={touches.champ}
            onChanger={touches.setRecherche}
            onFermer={touches.fermerLaRecherche}
            sousLaFrise={touches.marges.has('frise')}
          />
        )}

        {/*
         * En Carte et en Hiérarchie, à l'ordinateur, la barre de frappe se pose sur le fond, en
         * bas, au niveau de « + Liste » et « + Post-it » (retour d'Igor du 2026-10-20 : « la barre
         * de frappe doit etre au meme niveau que liste et post it sur le fond »).
         */}
        {barreSurLeFond &&
          (barreSortie ? (
            barre
          ) : (
            <button
              type="button"
              className="papiers-ajout feuille-bulle-ajout"
              title={AJOUTER_UNE_BULLE_ICI}
              onClick={() => setBarreSortie(true)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              {BULLE}
            </button>
          ))}

        {surFiche && !(ficheEnPage && surFiche.bulle.id === bulle.id) && (
          <FicheDeLaFeuille
            fiche={surFiche}
            centreId={bulle.id}
            couleur={couleur}
            fichesDuProjet={feuille.data.fiches}
            habitDuProjet={feuille.data.habit}
            gestes={gestes}
            navigation={navigation}
            allerALaFiche={allerALaFiche}
            onEntrer={entrer}
            onRemonterApresSuppression={() => naviguer(auDessus, { replace: true })}
          />
        )}
      </div>

      {!barreSurLeFond && !enCouche && barre}

      {gestes.inventaire && (
        <Inventaire
          inventaire={gestes.inventaire}
          onCocher={gestes.cocherRestee}
          onDecocher={gestes.decocherRestee}
          onIdee={gestes.garderCommeIdee}
          onDeplacer={gestes.deplacerAilleurs}
          onAnnuler={gestes.annulerValidation}
          annulation={gestes.annulation}
          onFermer={gestes.fermerInventaire}
        />
      )}
    </div>
  );
}
